/**
 * FeedbackModal – Allows users to add a comment to their feedback.
 * Mirrors the rate-loader FeedbackModal pattern.
 */
import React, { useState, useEffect, useRef } from 'react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  initialComment?: string;
  isDark?: boolean;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialComment = '',
  isDark = false,
}) => {
  const [comment, setComment] = useState(initialComment);
  const [submitted, setSubmitted] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setComment(initialComment);
      setSubmitted(false);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen, initialComment]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    onSubmit(comment.trim());
    setSubmitted(true);
  };

  const bg = isDark ? '#1e1e2e' : '#fff';
  const border = isDark ? '#2d2d3f' : '#e2e4f0';
  const textColor = isDark ? '#e5e7eb' : '#1f2937';
  const mutedColor = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#2d2d3f' : '#f9fafb';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: '16px',
          padding: '28px 32px',
          width: '420px',
          maxWidth: '90vw',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          animation: 'feedbackSlideUp 0.2s ease-out',
        }}
      >
        {!submitted ? (
          <>
            <h3
              style={{
                margin: '0 0 6px',
                fontSize: '17px',
                fontWeight: 700,
                color: textColor,
              }}
            >
              Add a Comment
            </h3>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: '13.5px',
                color: mutedColor,
                lineHeight: 1.45,
              }}
            >
              Your feedback helps us improve. Share any additional details below.
            </p>
            <textarea
              ref={textareaRef}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What could be improved?"
              rows={4}
              style={{
                width: '100%',
                resize: 'vertical',
                borderRadius: '10px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: textColor,
                padding: '10px 14px',
                fontSize: '14px',
                fontFamily: 'inherit',
                lineHeight: 1.55,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#196ECF';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(25,110,207,0.12)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '18px',
              }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  background: 'none',
                  color: mutedColor,
                  fontSize: '13.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!comment.trim()}
                style={{
                  padding: '8px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: !comment.trim()
                    ? isDark
                      ? '#374151'
                      : '#d1d5db'
                    : 'linear-gradient(135deg, #002677, #1a4fad)',
                  color: !comment.trim() ? mutedColor : '#fff',
                  fontSize: '13.5px',
                  fontWeight: 600,
                  cursor: comment.trim() ? 'pointer' : 'not-allowed',
                  transition: 'opacity 0.15s',
                }}
              >
                Submit
              </button>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 14px',
                fontSize: '24px',
                color: '#fff',
              }}
            >
              ✓
            </div>
            <h3
              style={{
                margin: '0 0 6px',
                fontSize: '17px',
                fontWeight: 700,
                color: textColor,
              }}
            >
              Thank You!
            </h3>
            <p
              style={{
                margin: '0 0 18px',
                fontSize: '13.5px',
                color: mutedColor,
              }}
            >
              Your feedback has been submitted.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 28px',
                borderRadius: '8px',
                border: 'none',
                background: 'linear-gradient(135deg, #002677, #1a4fad)',
                color: '#fff',
                fontSize: '13.5px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Okay
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes feedbackSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FeedbackModal;
